import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import CMSContentWrapper from "components/CMSContentWrapper";
import { titleize } from "util/strings";
import jsonld from "util/jsonld";
import { analyticsEvents, track } from "util/analytics";
import Layout from "components/layout";
import SearchResults from "components/PublicSearch/SearchResults";
import Pagination from "components/Pagination";
import { ListingMarker } from "components/GoogleMap/ListingMarker";
import ListingCard from "components/cards/ListingCard";
import cssVars from "styles/variables.json";

import {
  publicSearchPageQuery_city_cmsContent,
  publicSearchPageQuery_city_market,
  publicSearchPageQuery_city_market_neighborhoods,
  publicSearchPageQuery_listingSearch_listings,
} from "types/pages/office-space/publicSearchPageQuery";
import {
  areaQueryWithCmsContent_area_Market,
  areaQueryWithCmsContent_area_Market_neighborhoods,
  areaQueryWithCmsContent_area_Neighborhood_market,
  areaQueryWithCmsContent_area_Neighborhood_market_neighborhoods,
  areaQueryWithCmsContent_area_Submarket_market,
} from "types/pages/office-space/m/areaQueryWithCmsContent";
import { useCurrentUser } from "hooks";
import ChatCTA from "components/ChatModalCTA";
import ListingSearchForm from "./ListingSearchForm";
import styles from "./styles.module.scss";
import SaveSearchCTA from "./SavedSearchCTA";
import BaseMap from "../GoogleMap/BaseMap";

type Props = {
  city: string;
  page: number;
  area: string;
  state: string;
  limit: number;
  isOOM: boolean;
  count?: number;
  areaUuid?: string;
  numEmployees: string | null;
  neighborhoods:
    | (
        | publicSearchPageQuery_city_market_neighborhoods
        | areaQueryWithCmsContent_area_Market_neighborhoods
        | areaQueryWithCmsContent_area_Market_neighborhoods
        | areaQueryWithCmsContent_area_Neighborhood_market_neighborhoods
      )[];
  market:
    | publicSearchPageQuery_city_market
    | areaQueryWithCmsContent_area_Market
    | areaQueryWithCmsContent_area_Submarket_market
    | areaQueryWithCmsContent_area_Neighborhood_market
    | null;
  sanitizedCMS: publicSearchPageQuery_city_cmsContent | null;
  listings?: (publicSearchPageQuery_listingSearch_listings | null)[];
  submarkets: { id: string; externalId: string }[];
  seoString?: string;
};

export default function PublicSearch({
  city,
  page,
  area,
  state,
  limit,
  market,
  areaUuid,
  count = 0,
  numEmployees,
  sanitizedCMS,
  listings = [],
  isOOM = false,
  neighborhoods,
  submarkets,
}: Props) {
  const router = useRouter();
  const { currentUser } = useCurrentUser();
  const [selectedListingId, setSelectedListingId] = useState(null);
  const [hoveredListingId, setHoveredListingId] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [showSaveSearchCTA, setShowSaveSearchCTA] = useState(true);
  const [showSaveSearchForm, setShowSaveSearchForm] = useState(false);

  const trackSaveSearch = () => {
    track(analyticsEvents.clicked, {
      category: "Public Search CTAs",
      label: "Save Search",
    });
  };

  const shouldMapRender = () => {
    // Don't load the map by default on server by default, so it doesn't load on mobile by default
    if (
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia(`(min-width: ${cssVars["bp-mobile"]})`).matches
    ) {
      setShowMap(true);
    } else {
      setShowMap(false);
    }
  };

  useEffect(() => {
    track(analyticsEvents.search_view, {
      nonInteraction: 1,
      city,
      neighborhood: neighborhoods,
      num_employees: numEmployees,
    });
    shouldMapRender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generatePaginationLink = (pageNumber: number, label: string) => {
    const query = { page: pageNumber };
    const nhoodUuids =
      neighborhoods &&
      neighborhoods
        .filter(({ externalId }) => externalId !== areaUuid)
        .map(({ externalId }) => externalId)
        .join(",");
    const submarketUuids =
      submarkets &&
      submarkets
        .filter(({ externalId }) => externalId !== areaUuid)
        .map(({ externalId }) => externalId)
        .join(",");

    if (nhoodUuids) {
      Object.assign(query, {
        neighborhoods: nhoodUuids,
      });
    }

    if (submarketUuids)
      Object.assign(query, {
        submarkets: submarketUuids,
      });

    if (numEmployees) {
      Object.assign(query, { numEmployees });
    }

    return (
      <Link
        href={{
          pathname: router.asPath.split("?")[0],
          query,
        }}
        passHref
      >
        <a>{label}</a>
      </Link>
    );
  };

  const generateSearchParams = (index: number = 1) => {
    let search;
    if (typeof URLSearchParams !== "undefined") {
      search = new URLSearchParams(router.asPath.split("?")?.[1]);
      search.set("page", index.toString());
      search = search.toString();
    } else {
      search = `page=${index}`;
    }
    return search;
  };

  const generateMetadataLinks = () => {
    const prevPageNumber = page - 1;
    const nextPageNumber = page + 1;

    const nextParam = `?${generateSearchParams(nextPageNumber)}`;
    const previousParam =
      prevPageNumber < 2 ? "" : `?${generateSearchParams(prevPageNumber)}`;
    const canonicalParam = page >= 2 ? `?page=${page}` : "";
    const host =
      typeof window !== "undefined"
        ? `https://${window.location.hostname}`
        : "";
    const baseLink = `${host}${router.asPath.split("?")[0]}`;
    return {
      canonical: `${baseLink}${canonicalParam}`,
      previous: `${baseLink}${previousParam}`,
      next: `${baseLink}${nextParam}`,
    };
  };

  const getMetaData = () => {
    const url =
      market && market.seoString
        ? `office-space/m/${market.seoString}/${areaUuid}`
        : `${state}/${city}`;
    const breadcrumbItems = [
      {
        "@type": "ListItem",
        position: 1,
        item: {
          "@id": `https://www.squarefoot.com/${url}`,
          name: titleize(city || area),
        },
      },
    ];
    if (neighborhoods.length) {
      const nhoodAccessor = isOOM ? "parameterizedName" : "externalId";
      breadcrumbItems.push({
        "@type": "ListItem",
        position: 2,
        item: {
          "@id": `https://www.squarefoot.com/${url}/${neighborhoods[0][nhoodAccessor]}`,
          name: `${titleize(
            neighborhoods.map(({ name }) => name).join(", ")
          )}, ${titleize(city || market?.name || "")}`,
        },
      });
    }
    const metadata = [
      jsonld("BreadcrumbList", { itemListElement: breadcrumbItems }),
    ];
    listings.forEach((listing) => {
      const {
        id,
        building,
        coverImage,
        floorAndSuite,
        estimatedPsf,
        accuratePsf,
      } = listing!;
      metadata.push(
        jsonld("Product", {
          name: building.address,
          category: "Office Space", // spaceType
          image: coverImage?.url,
          offers: [
            {
              "@type": "Offer",
              name: `${building.address} ${floorAndSuite}`,
              url: `/building${building.slug}/${building.identity}#${id}`,
              price: accuratePsf || estimatedPsf || 0,
              priceCurrency: "USD",
              availableAtOrFrom: {
                "@type": "Place",
                address: {
                  "@type": "PostalAddress",
                  streetAddress: building.address,
                  addressLocality: building.city,
                  addressRegion: building.state,
                  postalCode: building.zip,
                },
              },
            },
          ],
        })
      );
    });

    return metadata;
  };

  const metaDataLinks = generateMetadataLinks();
  return (
    <>
      <Head>
        <title>
          {titleize(area)} Office Space for Rent - SquareFoot{" "}
          {page > 1 && ` - page ${page}`}
        </title>
        <meta
          name="description"
          content={`Explore${
            count ? ` ${count} ` : " "
          }office space listings in ${titleize(
            area
          )}. Find the perfect space for your business at SquareFoot.`}
        />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(getMetaData()) }}
        />

        <link rel="canonical" href={metaDataLinks.canonical} />
        {page < Math.ceil(count / limit) && (
          <link rel="next" href={metaDataLinks.next} />
        )}
        {page > 1 && <link rel="prev" href={metaDataLinks.previous} />}
      </Head>

      <Layout>
        <ChatCTA
          loggedIn={!!currentUser}
          city={city}
          neighborhoods={neighborhoods || []}
          submarkets={submarkets || []}
          market={market?.id}
          numEmployees={numEmployees ? parseInt(numEmployees, 10) : 0}
        />

        {showSaveSearchCTA && !currentUser && (
          <SaveSearchCTA
            showForm={showSaveSearchForm}
            city={city}
            neighborhoods={neighborhoods || []}
            submarkets={submarkets || []}
            market={market?.id}
            numberOfEmployees={numEmployees ? parseInt(numEmployees, 10) : 0}
            onCancel={() => {
              setShowSaveSearchCTA(false);
              setShowSaveSearchForm(false);
            }}
          />
        )}
        <div className={`inset-layout ${styles.container}`}>
          <div className={styles.listings}>
            <ListingSearchForm
              selectedMarket={market!}
              subMarket={titleize(area)}
              state={state?.toUpperCase()}
              numEmployees={numEmployees}
              neighborhoods={neighborhoods || []}
              isOOM={isOOM}
              submarkets={submarkets}
              city={titleize(city || "")}
            />
            <SearchResults
              loggedIn={!!currentUser}
              listings={listings}
              count={count}
              area={area}
              areaUuid={areaUuid}
              page={page}
              limit={limit}
              city={city}
              neighborhoods={neighborhoods}
              submarkets={submarkets}
              market={market}
              onHoverListing={(hoverListing) =>
                setHoveredListingId(hoverListing ? hoverListing.id : null)
              }
            >
              <div className={styles.searchResultButtonContainer}>
                {!showMap && (
                  <button
                    type="button"
                    className={`btn-text ${styles.showMapButton}`}
                    onClick={() => setShowMap(true)}
                  >
                    <i className="icon icon-map" />
                    <span className={styles.showMapButtonText}>Show Map</span>
                  </button>
                )}
                {!showSaveSearchCTA && !currentUser && (
                  <button
                    type="button"
                    onClick={() => {
                      trackSaveSearch();
                      setShowSaveSearchCTA(true);
                      setShowSaveSearchForm(true);
                    }}
                    className={`btn-primary ${styles.showSaveSearchCTAButton}`}
                  >
                    Save Search
                  </button>
                )}
                {showMap && (showSaveSearchCTA || !!currentUser) && (
                  <div className={styles.mobileButtonSkeleton} />
                )}
              </div>
            </SearchResults>
            <Pagination
              paginationType="page"
              itemsPerPage={limit}
              position={page}
              total={count}
              getLink={generatePaginationLink}
              maxLinks={10}
            />
          </div>
          {showMap && (
            <BaseMap
              autoCenter
              className={styles.map}
              style={{ height: "auto" }}
            >
              {listings.map(
                (listing) =>
                  listing !== null && (
                    <ListingMarker
                      key={`marker-${listing.id}`}
                      latitude={listing.building.lat}
                      longitude={listing.building.lng}
                      listing={listing}
                      isExpanded={selectedListingId === listing.id}
                      isHovered={hoveredListingId === listing.id}
                      selectListing={(id) =>
                        setSelectedListingId(
                          selectedListingId === id ? null : id
                        )
                      }
                      hoverListing={(hoverListing) =>
                        setHoveredListingId(
                          hoverListing ? hoverListing.id : null
                        )
                      }
                      showAccuratePrices={false}
                      renderExpandedMarker={(
                        expandedListing,
                        showAccuratePrices,
                        mapCardStyles
                      ) => (
                        <a
                          href={`/building${expandedListing.building.slug}/${expandedListing.building.identity}?listingId=${expandedListing.id}`}
                        >
                          <ListingCard
                            listing={expandedListing}
                            className={mapCardStyles}
                          />
                        </a>
                      )}
                    />
                  )
              )}
            </BaseMap>
          )}
        </div>
        {sanitizedCMS?.content && (
          <CMSContentWrapper
            className="inset-layout"
            sanitizedTitle={sanitizedCMS.title}
            sanitizedContent={sanitizedCMS.content}
            faq={sanitizedCMS?.faq || []}
          />
        )}
      </Layout>
      <span>HALLO</span>
    </>
  );
}
